import { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import restartIcon from '../../../assets/icon_restart@2x.png';
import { closeWebsocket, disposeWebsocket, reconnectWebsocket, startChat, wsSend } from '../../../utils/net';
import style from './chatBoard.module.css';
import {
    assistantChatStatusAtom,
    canStartWsAtom,
    chatGuessAtom,
    chatHistoryAtom,
    chatImageURLAtom,
    chatLangAtom,
    chatTextAtom,
    descriptionAtom,
    guessChatStatusAtom,
    lastGenerateUUIDAtom,
    logInfoAtom,
    modalObjAtom,
    needStartWsAtom,
    posterGenerateAtom,
    promptAtom,
    setGenerateAtom,
    showProgressAtom,
    showRegenerateAtom,
    stopChatAtom,
    taskDetailAtom,
    taskInitAtom,
} from '../../../store';
import { exportToImage, setChatImage } from '../../../utils/format';
import { useTips } from '../../../common/GlobalTips';
import { XScroll } from '../../widgets/XScroll';
import { trackEvent } from '../../../utils/trackEvent'
import { AvatarImage } from '../../../common/AvatarImage';
import { useTranslation } from "react-i18next";

function ChatBoard() {
    const logInfo = useRecoilValue(logInfoAtom);
    const [modalObj, setModalObj] = useRecoilState(modalObjAtom);
    const [chatHistory, setChatHistory] = useRecoilState(chatHistoryAtom);
    const [showRegenerate] = useRecoilState(showRegenerateAtom);
    const [chatGuess, setChatGuess] = useRecoilState(chatGuessAtom);
    const [canStartWs, setCanStartWs] = useRecoilState(canStartWsAtom);
    const prompt = useRecoilValue(promptAtom);
    const [showGuess, setShowGuess] = useState(true);
    const [taskInit, setTaskInit] = useRecoilState(taskInitAtom);
    const setTaskDetail = useSetRecoilState(taskDetailAtom);
    const [stopChat, setStopChat] = useRecoilState(stopChatAtom);
    const [chatText, setChatText] = useRecoilState(chatTextAtom);
    const taskDetail = useRecoilValue(taskDetailAtom);
    const guessChatStatus = useRecoilValue(guessChatStatusAtom);
    const [assistantChatStatus, setAssistantChatStatus] = useRecoilState(assistantChatStatusAtom);
    const [chatLang, setChatLang] = useRecoilState(chatLangAtom);
    const setChatImageURL = useSetRecoilState(chatImageURLAtom);
    const setNeedStartWs = useSetRecoilState(needStartWsAtom);
    const [posterGenerate, setPosterGenerate] = useRecoilState(posterGenerateAtom);
    const chatMessagesRef = useRef();
    const generate = useRecoilValue(setGenerateAtom);
    const [description, setDescription] = useRecoilState(descriptionAtom);
    const setLastGenerateUUID = useSetRecoilState(lastGenerateUUIDAtom);
    const setShowProgress = useSetRecoilState(showProgressAtom);
    const textAreaRef = useRef(null);
    const tip = useTips();
    const chatRef = useRef();
    const containerRef = useRef(null)
    const { t ,i18n } = useTranslation();
    const [containerHeights, setContainerHeights] = useState(58);
    // const avatarURL = taskDetail?.author?.avatar_url ? taskDetail.author.avatar_url : defaultAvatarURL

    const handleIpt = (ev) => {
        setChatText(ev.target.value);
    };

    const handleSend = (msg) => {
        if (!msg || assistantChatStatus !== '[END]') return;
        trackEvent('ChatAvatar - Chat - Send', msg)
        setChatGuess([])
        _czc.push(['_trackEvent', 'Card', 'Send', 'Generate Card']);
        wsSend({
            task_uuid: taskInit.task_uuid,
            content: msg,
            language: chatLang,
            summary: prompt,
        });

        setChatHistory({
            ...chatHistory,
            [Date.now()]: {
                chat_uuid: Date.now(),
                provider: 'user',
                content: msg,
                timeStamp: Date.now(),
            },
        });

        setChatText('');
        setShowGuess(false);
        setAssistantChatStatus(false);
    };

    useEffect(() => {
        if (assistantChatStatus=== '[END]' && description) {
            handleSend(description)
            setDescription('')
        }
    }, [ description,assistantChatStatus])


    const handleSelectTip = (value) => () => {
        _czc.push(['_trackEvent', 'Card', 'Choose Hint', 'Generate Card']);
        setChatText(value.substring(3));
    };

    useEffect(() => {
        // if (stopChat !== 2) return
        if (guessChatStatus === '[START]') setShowGuess(true);
    }, [guessChatStatus]);

    const handleChangeLang = (lang) => {
        if (lang === chatLang) return;
        if (!canStartWs) {
            tip({
                type: 'warning',
                content: t('TIP_WRAN_CHATBOARD_SWITCHING'),
            });
            return;
        }
        _czc.push(['_trackEvent', 'Card', 'Language Change', 'Generate Card']);
        setChatLang(lang);
        handleRestart();
    };

    const handleRestart = async () => {
        if (generate) return;
        if (!canStartWs) {
            tip({
                type: 'warning',
                content: t('TIP_WRAN_CHATBOARD_RESTART'),
            });
            return;
        }
        setCanStartWs(false);
        _czc.push(['_trackEvent', 'Card', 'Restart', 'Generate Card']);
        closeWebsocket();
        disposeWebsocket();
        const response = await startChat();
        setNeedStartWs(true);
        setStopChat(false);
        setTaskInit(response.data);
        setShowProgress(false);
        setTaskDetail(false);
        setLastGenerateUUID(false);
        setPosterGenerate(false);
        setModalObj({
            ...modalObj,
            type: 'generate',
            state: true
        });
        // setIsShowModal(true);
        // window.history.pushState(null, '', '/')
    };

    const handleRegenerate = () => {
        _czc.push(['_trackEvent', 'Card', 'Regenerate', 'Generate Card']);
        setTaskDetail(false);
        setStopChat(false);
        setLastGenerateUUID(false);
        reconnectWebsocket();
        setPosterGenerate(false);

        setModalObj({
            ...modalObj,
            type: 'generate',
            state: true
        });
        // setIsShowModal(true);
    };

    window.exportChat = async () => {
        await exportToImage(chatRef.current, 'chat');
    };

    const allAssistantMessages = Object.values(chatHistory).every(
        (chat) => chat.provider === 'assistant',
    );

    useEffect(() => {
        if (posterGenerate) {
            setChatImage(chatRef.current).then((url) => {
                setChatImageURL(url)
            })
        }
    }, [posterGenerate]);

    useEffect(() => {
        if (chatMessagesRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
    }, [chatHistory, chatGuess]);


    useEffect(() => {
        setContainerHeights(58)
        if (chatGuess.length && containerRef.current) {
            const children = containerRef.current.children
            let newHeight = containerHeights
            for (let i = 0; i < children.length; i++) {
                newHeight = Math.max(children[i].getBoundingClientRect().height, newHeight)
            }
            setContainerHeights(newHeight);
        }
    }, [chatGuess.length, containerRef.current]);


    useEffect(
        () => () => {
            setChatText('');
        },
        [],
    );

    const historyavatarStyle = {
        borderRadius: "50%",
        width: "40px",
        height: "40px",
        border: " 1px solid #e7e3f1",
        flexShrink: "0",
    }

    const endSpanStyle = {
        color: '#aaa',
        fontSize: '16px',
        textAlign: "center"
    }

    const historyLineStyle = {
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: "16px",
    }

    const chatContentStyle = {
        padding: "12px 16px",
        textAlign: "left",
        borderRadius: "6px",
        fontSize: "16px",
        lineHeight: "20px",
        color: "#434343"
    }

    const chatscrollcontentStyle = {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
    }

    const handlerToggleLang = (lang) => {
        handleChangeLang(lang)
        // if(Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone === 'Asia/Shanghai'){
        //     if(lang==='Chinese'){
        //         i18n.changeLanguage("zh")
        //     }else if(lang==='English'){
        //         i18n.changeLanguage("en")
        //     }
        // }
    }

    const handleShowRegTip = () => tip({
        type: 'primary',
        content: t('TIP_PRIMARY_MODEL_REPLACE'),
        yes: handleRegenerate,
        no: (f) => f,
    })

    return (
        <div className={style.chatBox}>
            <div className={style.chatTitleBox}>
                <div
                    style={{
                        padding: '3px 0',
                    }}
                >
                    {' '}
                    Chat
                </div>
                {(taskInit) ? (
                    <div
                        className={`${style.restart} ${generate ? style.notAllow : ''}`}
                        style={{
                            cursor: generate ? 'not-allowed' : '',
                        }}
                        onPointerDown={handleRestart}
                    >
                        {/* <HiRefresh size='1em' /> */}
                        <img className={style.restartIcon} src={restartIcon} alt="restart" />
                        &nbsp;Restart
                    </div>
                ) : null}

                <div className={style.spaceholder} />

                {taskDetail || stopChat ? null : (
                    <>
                        <div
                            onClick={handlerToggleLang.bind(null, 'Chinese')}
                            data-cy="generate-chat-language-chinese"
                            className={`${style.lang} ${chatLang === 'Chinese' ? style.selected : ''
                                } ${!canStartWs ? style.disabled : ''}`}
                        >
                            中文
                        </div>
                        <div>|</div>
                        <div
                            onClick={handlerToggleLang.bind(null, 'English')}
                            data-cy="generate-chat-language-english"
                            className={`${style.lang} ${style.en} ${chatLang === 'English' ? style.selected : ''
                                } ${!canStartWs ? style.disabled : ''}`}
                        >
                            English
                        </div>
                    </>
                )}

                {(taskDetail && taskInit && showRegenerate) ? (
                    <div
                        className={style.regene}
                        onClick={handleShowRegTip}
                    >
                        Regenerate
                    </div>
                ) : null}
            </div>

            <div className={style.chatCon} ref={chatRef}>
                <div style={{ ...chatscrollcontentStyle }} className={`${style.chatMsgCon} ${style.noScrollbar}`} ref={chatMessagesRef}>
                    {(!allAssistantMessages || !stopChat)
                        && Object.values(chatHistory)
                            .sort((a, b) => a.timeStamp - b.timeStamp)
                            .filter((chat, idx, arr) => (
                                !(chat.provider === 'assistant' && idx === arr.length - 1)
                                || !stopChat
                            ))
                            .map((item, index) => {
                                let userFlag = item.provider === "user"
                                const extStyle1 = {
                                    flexDirection: userFlag ? "row-reverse" : "row"
                                }

                                const extStyle2 = {
                                    backgroundColor: userFlag ? "#fff" : "#E7E3F1",
                                }
                                return (
                                    <div key={index} className={style.historyLine}
                                        style={{ ...historyLineStyle, ...extStyle1 }}>
                                        <div className={style.historyavatar}
                                            style={historyavatarStyle}>

                                            {userFlag
                                                ? <AvatarImage width='100%' height='100%' url={(taskDetail ? taskDetail?.author?.avatar_url : logInfo?.avatar_url) || '../../assets/defaultAvatar.png'} />
                                                : <AvatarImage width='100%' height='100%' assitant />}
                                            {/* {userFlag ? <UserAvatarImg /> : <AssitantAvatarImg />} */}

                                        </div>
                                        <div className={style.chatContent}
                                            style={{ ...chatContentStyle, ...extStyle2 }}>
                                            {item.content}
                                        </div>
                                    </div>
                                )
                            })}
                    {stopChat && (Object.keys(chatHistory).length <= 1 || allAssistantMessages) && (
                        <div className={`${style.assistant} ${style.notificationText}`}>
                            <div className={`${style.notificationBubble} ${style.unactive}`}>
                                {t('CHATAVATAR_BOARD_NO_CONVER_TIP')}
                            </div>
                        </div>
                    )}

                    {stopChat
                        && !(Object.keys(chatHistory).length <= 1 || allAssistantMessages) && (
                            <span style={endSpanStyle}>{t('CHATAVATAR_BOARD_END_CONVER_TIP')}</span>
                        )}

                    {!assistantChatStatus && !stopChat && (
                        <div className={`${style.chatMsgRow} ${style.assistant}`}>
                            <div className={style.avatarAssistant}>
                                <img
                                    src="/assets/avatar.png"
                                    alt="avatar"
                                />
                            </div>
                            <div className={`${style.bubble}`}>
                                <span className={style.dot} style={{ animationDelay: '0s' }}>
                                    .
                                </span>
                                <span className={style.dot} style={{ animationDelay: '0.33s' }}>
                                    .
                                </span>
                                <span className={style.dot} style={{ animationDelay: '0.66s' }}>
                                    .
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {!stopChat ? (
                    <div className={style.chatIptCon}>
                        <XScroll
                            containerHeights={containerHeights}
                            customStyle={{
                                width: '100%',
                                height: containerHeights,
                            }}
                            contentStyle={'widthAuto'}
                            scrollBarType={'scroll'}
                        >
                            <div
                                className={style.chatTipsCon}
                                ref={containerRef}>
                                {chatGuess
                                    .filter((c) => c)
                                    .map((guess) => {
                                        const substringGuess = guess.substring(3);
                                        if (substringGuess && showGuess) {
                                            return (
                                                <span
                                                    className={style.chatTips}
                                                    key={guess}
                                                    onClick={handleSelectTip(guess)}
                                                >
                                                    {substringGuess}
                                                </span>
                                            );
                                        }
                                        return null;
                                    })}
                            </div>
                        </XScroll>

                        <div className={style.chatRowCon}>
                            <div className={style.iptLineCon}>
                                <div className={style.iptSpaceholder}>{chatText || 'X'}</div>
                                <textarea
                                    className={style.ipt}
                                    ref={textAreaRef}
                                    onChange={(e) => {
                                        setChatText(e.currentTarget.value);
                                    }}
                                    value={chatText}
                                    data-cy="generate-chat-input"
                                    placeholder="Describe your avatar"
                                    onKeyDown={(e) => {
                                        handleIpt(e);
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (e.shiftKey) {
                                                setChatText((prevChatText) => `${prevChatText}\n`);
                                            } else {
                                                handleSend(chatText);
                                            }
                                        }
                                    }}
                                />
                            </div>

                            <div
                                className={`${style.btn} ${!chatText || assistantChatStatus !== '[END]'
                                    ? style.disabled
                                    : ''
                                    }`}
                                data-cy="generate-chat-send"
                                onPointerDown={handleSend.bind(null, chatText)}
                            >
                                Send
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div >
    );
}

export { ChatBoard };
